import React from 'react';
import {
	CerebroDatePicker,
	CerebroDrawer,
	CerebroForm,
	CerebroHeading,
	CerebroLabel,
	CerebroMaskedInput,
	CerebroPrimaryButton,
	CerebroText,
} from '../../shared/components';
import { Deal, DealInvoice, ProductSubscription } from '../../shared/models';
import { useApi } from '../../shared/context';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import { Field, Formik, FormikErrors } from 'formik';
import { DateUtils, FormikUtils, NumberUtils } from '../../shared/utils';
import { Grid } from '@mui/material';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { QueryKeys } from '../../shared/hooks';
import { MaxCurrencyLength } from '../../shared/utils/platform';
import { getApiResponseData } from '../../shared/utils/axios';
import { validateDateWithinPlusMinus100Years } from '../../shared/utils/date';

interface CreateSuccessFeeInvoiceDrawerProps {
	subscription: ProductSubscription | null;
	deal: Deal;
	onClose: () => void;
}

export const CreateSuccessFeeInvoiceDrawer: React.FC<CreateSuccessFeeInvoiceDrawerProps> = ({
	subscription,
	deal,
	onClose,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const ref = React.useRef<HTMLDivElement>(null);
	const projectedFundDate = deal.isAsapTargetDate ? new Date().toISOString().split('T')[0] : deal.targetFundingDate;

	const createInvoiceMutation = useMutation(
		async ({ values }: CreateSuccessFeeInvoiceMutationVariables) => {
			const dto = {
				amountFunded: NumberUtils.ParseDecimal(values.amountFunded, undefined),
				projectedFundDate: values.projectedFundDate
					? values.projectedFundDate.toISOString().split('T')[0]
					: null,
			};

			const { data, error } = await getApiResponseData<DealInvoice>(
				api.post(CerebroApiEndpoints.Deals.CreateSuccessFeeInvoice(deal.guid, subscription?.guid), dto)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.Deal.Subscriptions(deal.guid));
				client.invalidateQueries(QueryKeys.Deal.UpdateStatus(deal.guid));
				snackbar.enqueueSnackbar('Success fee invoice successfully created.', { variant: 'success' });
				onClose();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
		}
	);

	return (
		<CerebroDrawer ref={ref} open={subscription != null} onClose={onClose}>
			<CerebroHeading>Create Success Fee Invoice</CerebroHeading>

			<Formik
				initialValues={
					{
						amountFunded: '0',
						projectedFundDate: DateUtils.ParseDateOnly(projectedFundDate),
					} as CreateSuccessFeeInvoiceFormValues
				}
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(createInvoiceMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit }) => (
					<CerebroForm encType="multipart/form-data" onSubmit={handleSubmit}>
						<Grid
							container
							spacing={3}
							sx={{ marginTop: 3 }}
							justifyContent={{ xs: 'center', md: 'flex-start' }}
						>
							<Grid item xs={6}>
								<CerebroLabel required id="projectedFundDate">
									Projected Fund Date
								</CerebroLabel>
								<Field
									component={CerebroDatePicker}
									id="projectedFundDate"
									name="projectedFundDate"
									disabled={isSubmitting}
									error={FormikUtils.IsInvalid('projectedFundDate', errors, touched)}
								/>
								{FormikUtils.IsInvalid('projectedFundDate', errors, touched) && (
									<CerebroText variant="error">{errors.projectedFundDate}</CerebroText>
								)}
							</Grid>
							<Grid item xs={6}></Grid>
							<Grid item xs={6}>
								<CerebroLabel required id="amountFunded">
									Amount Funded
								</CerebroLabel>
								<Field
									formatas="currency"
									as={CerebroMaskedInput}
									maxLength={MaxCurrencyLength}
									id="amountFunded"
									name="amountFunded"
									disabled={isSubmitting}
									error={FormikUtils.IsInvalid('amountFunded', errors, touched)}
								/>
								{FormikUtils.IsInvalid('amountFunded', errors, touched) && (
									<CerebroText variant="error">{errors.amountFunded}</CerebroText>
								)}
							</Grid>
							<Grid item xs={6}></Grid>
						</Grid>

						<CerebroPrimaryButton type="submit" loading={isSubmitting} sx={{ marginTop: 6 }}>
							Save
						</CerebroPrimaryButton>
					</CerebroForm>
				)}
			</Formik>
		</CerebroDrawer>
	);
};

var validateForm = (values: CreateSuccessFeeInvoiceFormValues): CreateSuccessFeeInvoiceFormErrors => {
	const errors: CreateSuccessFeeInvoiceFormErrors = {};

	if (!values.projectedFundDate) {
		errors.projectedFundDate = 'Projected funding date is required.';
	} else {
		
		const standardError = validateDateWithinPlusMinus100Years(values.projectedFundDate, 'Projected funding date');
		if (standardError) {
			errors.projectedFundDate = standardError;
		} else if (values.projectedFundDate <= new Date()) {
			errors.projectedFundDate = 'Projected funding date should be in the future.';
		}
	}

	if (!values.amountFunded) {
		errors.amountFunded = 'Funding amount is required.';
	}

	return errors;
};

type CreateSuccessFeeInvoiceFormErrors = FormikErrors<CreateSuccessFeeInvoiceFormValues>;
type CreateSuccessFeeInvoiceMutationVariables = FormikUtils.MutateOnSubmitVariables<CreateSuccessFeeInvoiceFormValues>;
interface CreateSuccessFeeInvoiceFormValues {
	amountFunded: string;
	projectedFundDate: Date | null;
}
